import React, { useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputBase from '@material-ui/core/InputBase';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { HistoryChats } from 'features/ai-assistant/components';
import { useAIAssistantDrawer } from 'features/ai-assistant/components/AIAssistantDrawer/AIAssistantDrawer.hooks';
import { getAssistantEndpoints } from 'features/ai-assistant/constants';
import { useSearchConversation } from 'features/ai-assistant/hooks';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import { UIStatus } from 'UI/constants/status';

import { AI_PROSPECTING_ASSISTANT_BASE_PATH } from '../../Prospecting.constants';

import { useStyles } from './ConversationsModal.styled';

const renderExcerptHtml = excerpt => {
  if (!excerpt) {
    return null;
  }
  return excerpt.includes('**')
    ? excerpt.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    : excerpt;
};

const COPIES = strings.ai.generators.assistant;

export const ConversationsModal = ({ onClose, onDelete, onSelect, open, sessionID }) => {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const isSearching = query.length > 0;

  const { sessionSearch: searchEndpoint, ...endpoints } = getAssistantEndpoints(
    AI_PROSPECTING_ASSISTANT_BASE_PATH
  );

  const {
    chats,
    currentSessionId,
    fetchMoreConversations,
    handleClickConversation,
    handleDeleteConversation,
    handlePinConversation,
    handleRenameConversation,
    hasMoreConversations,
    isLoadingMoreConversations,
    uiStatus: listConversationsUiStatus
  } = useAIAssistantDrawer({ apiInstance: SECONDARY_API_CLIENT, endpoints, sessionID, onDelete });

  const { results: searchResults, uiStatus: searchUiStatus } = useSearchConversation({
    apiInstance: SECONDARY_API_CLIENT,
    endpoint: searchEndpoint,
    query,
    debounceMs: 300
  });

  const isLoading =
    listConversationsUiStatus === UIStatus.Loading || searchUiStatus === UIStatus.Loading;

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;

    onClose && onClose();
  };

  const handleSelectConversation = (sessionId, conversation) => {
    if (!sessionId) return;

    const selectedChat = chats.find(chat => chat.sessionId === sessionId) || conversation;
    onSelect && onSelect({ sessionId, type: selectedChat?.type });
    handleClickConversation(sessionId);
  };

  const handleClear = () => {
    setQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper }}
      onClose={handleClose}
      open={open}
      scroll="paper"
    >
      <Box className={classes.header}>
        <Typography variant="h6">{COPIES.chatHistory}</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className={classes.searchInputContainer}>
        <SearchIcon color="disabled" fontSize="small" />
        <InputBase
          autoFocus
          className={classes.searchInput}
          placeholder={COPIES.sidebar.searchChat}
          value={query}
          onChange={event => setQuery(event.target.value)}
          inputRef={inputRef}
          endAdornment={
            query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label={COPIES.search.clear}
                  className={classes.clearButton}
                  size="small"
                  onClick={handleClear}
                  onMouseDown={event => event.preventDefault()}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
        />
      </Box>

      <DialogContent className={classes.dialogContent}>
        {isLoading && (
          <Box className={classes.statusRow}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="textSecondary">
              {COPIES.search.searching}
            </Typography>
          </Box>
        )}

        <When condition={isSearching}>
          {searchUiStatus === UIStatus.Default && (
            <Typography variant="body2" color="textSecondary">
              {COPIES.search.default}
            </Typography>
          )}

          {searchUiStatus === UIStatus.Empty && (
            <Typography variant="body2" color="textSecondary">
              {COPIES.search.empty}
            </Typography>
          )}

          {searchUiStatus === UIStatus.Success && (
            <List disablePadding>
              {searchResults.map(conversation => (
                <ListItem
                  key={conversation.date}
                  button
                  component="li"
                  className={classes.resultItem}
                  onClick={() => handleSelectConversation(conversation?.sessionId, conversation)}
                >
                  <ListItemText
                    primary={conversation.sessionTitle}
                    secondary={
                      conversation.excerpt ? (
                        <Typography
                          component="span"
                          variant="body2"
                          dangerouslySetInnerHTML={{
                            __html: renderExcerptHtml(conversation.excerpt)
                          }}
                        />
                      ) : null
                    }
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </When>

        <When condition={!isSearching}>
          <When
            condition={
              listConversationsUiStatus === UIStatus.Success ||
              listConversationsUiStatus === UIStatus.Empty
            }
          >
            <HistoryChats
              hasMoreConversations={hasMoreConversations}
              historyChats={chats}
              isLoadingMoreConversations={isLoadingMoreConversations}
              onClickConversation={handleSelectConversation}
              onDeleteConversation={handleDeleteConversation}
              onFetchMore={fetchMoreConversations}
              onPinConversation={handlePinConversation}
              onRenameConversation={handleRenameConversation}
              selectedChatId={currentSessionId}
            />
          </When>
        </When>
      </DialogContent>
    </Dialog>
  );
};
